import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0b4ac80e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 0,
  id: "datastore-import-dropzone",
  class: "datastore-dropzone",
  for: "datastore-import-file"
}
const _hoisted_3 = {
  key: 1,
  class: "datastore-file-chip"
}
const _hoisted_4 = { class: "datastore-hint" }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "datastore-map-sample" }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = { class: "datastore-report" }
const _hoisted_9 = { class: "datastore-report-line" }
const _hoisted_10 = { class: "datastore-report-line" }
const _hoisted_11 = { class: "datastore-ok" }
const _hoisted_12 = { class: "datastore-report-line" }
const _hoisted_13 = { class: "datastore-warn" }
const _hoisted_14 = {
  key: 0,
  class: "datastore-error-list"
}
const _hoisted_15 = { class: "datastore-error-lines" }
const _hoisted_16 = { class: "datastore-hint" }
const _hoisted_17 = { key: 3 }
const _hoisted_18 = { class: "datastore-file-chip" }
const _hoisted_19 = { class: "datastore-report-line margin-top-10" }
const _hoisted_20 = {
  key: 0,
  class: "datastore-error-list margin-top-10"
}
const _hoisted_21 = { class: "datastore-error-lines" }

import { computed, nextTick, onUnmounted, ref } from "vue";
import {
  ElButton,
  ElDialog,
  ElOption,
  ElProgress,
  ElRadio,
  ElRadioGroup,
  ElSelect,
  ElStep,
  ElSteps,
  ElTable,
  ElTableColumn,
  ElTag,
} from "element-plus";
import { TYPE } from "vue-toastification";
import { showAlert } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import {
  ColumnMapping,
  DataStoreJob,
  DataStoreTable,
  ImportPreview,
  MAP_CREATE_NEW,
  MAP_SKIP,
} from "@/datastore/types";
import { parseCsvPreview } from "@/datastore/utils/csvPreviewParse";
import {
  defaultColumnMapping,
  duplicateTargets,
  isMappingUsable,
} from "@/datastore/utils/columnMap";
import { validateCell } from "@/datastore/utils/cellValidate";
import {
  cancelJob,
  getJob,
  runImportJob,
  startImport,
} from "@/datastore/services/datastore.service";


export default /*#__PURE__*/_defineComponent({
  __name: 'ImportWizard',
  props: {
    table: {},
    modelValue: { type: Boolean }
  },
  emits: ["update:modelValue", "changed"],
  setup(__props: any, { emit: __emit }) {

const { t } = i18n.global;

const props = __props;
const emit = __emit;

const step = ref(0);
const preview = ref<ImportPreview | null>(null);
const mapping = ref<ColumnMapping[]>([]);
const importMode = ref<"append" | "upsert">("append");
const errorMode = ref<"partial" | "strict">("partial");
const dryRun = ref<{ sampled: number; ok: number; errors: string[] }>({
  sampled: 0,
  ok: 0,
  errors: [],
});
const job = ref<DataStoreJob | null>(null);
const mapTable = ref<InstanceType<typeof ElTable> | null>(null);
let pollTimer: ReturnType<typeof setInterval> | null = null;

const primaryFieldName = computed(
  () => props.table.fields.find((f) => f.isPrimary)?.name ?? "id"
);

function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}
function formatBytes(n: number): string {
  if (n > 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} GB`;
  if (n > 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  return `${Math.ceil(n / 1000)} KB`;
}
function sampleFor(colIdx: number): string {
  return preview.value?.sampleRows[0]?.[colIdx] ?? "";
}
function typeFor(m: ColumnMapping): string {
  if (m.target === MAP_SKIP) return "—";
  if (m.target === MAP_CREATE_NEW) return m.inferredType;
  return props.table.fields.find((f) => f.id === m.target)?.type ?? "";
}

async function onFilePicked(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  // The browser only reads a slice for the mapping preview — the actual
  // import streams the raw file to the server (design doc §7.1).
  const head = await file.slice(0, 256 * 1024).text();
  const parsed = parseCsvPreview(head, 100);
  if (parsed.header.length === 0) {
    showAlert(TYPE.ERROR, t("DataStore.EmptyCsv"));
    return;
  }
  preview.value = await startImport(
    props.table.id,
    file,
    parsed.header,
    parsed.rows
  );
  input.value = "";
}

async function toMapping() {
  if (!preview.value) return;
  if (mapping.value.length === 0) {
    mapping.value = defaultColumnMapping(
      preview.value.header,
      preview.value.sampleRows,
      props.table.fields
    );
  }
  step.value = 1;
  // The table mounts with this step, so its first column measurement happens
  // before the step is laid out; re-measure once it is on screen.
  await nextTick();
  if (mapTable.value) {
    mapTable.value.doLayout();
  }
}

function toDryRun() {
  if (!isMappingUsable(mapping.value)) {
    showAlert(TYPE.WARNING, t("DataStore.MappingUnusable"));
    return;
  }
  const dupes = duplicateTargets(mapping.value);
  if (dupes.length > 0) {
    showAlert(TYPE.WARNING, t("DataStore.MappingDuplicates"));
    return;
  }
  const rows = preview.value?.sampleRows ?? [];
  const errors: string[] = [];
  let ok = 0;
  rows.forEach((row, rowIdx) => {
    let rowOk = true;
    mapping.value.forEach((m, colIdx) => {
      if (m.target === MAP_SKIP || m.target === MAP_CREATE_NEW) return;
      const field = props.table.fields.find((f) => f.id === m.target);
      if (!field) return;
      const result = validateCell(row[colIdx], field);
      if (!result.ok) {
        rowOk = false;
        if (errors.length < 30) {
          errors.push(
            `${t("DataStore.RowN", { n: rowIdx + 2 })}: ${result.error}`
          );
        }
      }
    });
    if (rowOk) ok++;
  });
  dryRun.value = { sampled: rows.length, ok, errors };
  step.value = 2;
}

async function startJob() {
  if (!preview.value) return;
  await runImportJob(
    preview.value.jobId,
    mapping.value,
    preview.value.sampleRows,
    importMode.value,
    errorMode.value
  );
  step.value = 3;
  pollTimer = setInterval(async () => {
    if (!preview.value) return;
    job.value = await getJob(preview.value.jobId);
    if (
      job.value.state === "FINISHED" ||
      job.value.state === "FAILED" ||
      job.value.state === "CANCELLED"
    ) {
      stopPolling();
      if (job.value.state === "FINISHED") {
        showAlert(
          TYPE.SUCCESS,
          t("DataStore.ImportFinished", {
            rows: formatCount(job.value.totalRows),
          })
        );
        emit("changed");
      }
    }
  }, 700);
  job.value = await getJob(preview.value.jobId);
}

async function onCancelJob() {
  if (!preview.value) return;
  await cancelJob(preview.value.jobId);
  job.value = await getJob(preview.value.jobId);
  stopPolling();
  showAlert(
    TYPE.WARNING,
    t("DataStore.ImportCancelled", {
      rows: formatCount(job.value?.processedRows ?? 0),
    })
  );
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function reset() {
  step.value = 0;
  preview.value = null;
  mapping.value = [];
  job.value = null;
  dryRun.value = { sampled: 0, ok: 0, errors: [] };
  stopPolling();
}

function close() {
  // The job (if any) keeps running server-side; only the dialog closes.
  emit("update:modelValue", false);
  if (job.value?.state === "FINISHED" || !job.value) reset();
}

function onVisibilityChange(visible: boolean) {
  emit("update:modelValue", visible);
  if (!visible && (job.value?.state === "FINISHED" || !job.value)) reset();
}

onUnmounted(stopPolling);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ElDialog), {
    "model-value": _ctx.modelValue,
    title: _unref(t)('DataStore.ImportInto', { name: _ctx.table.name }),
    width: "680px",
    "custom-class": "datastore-dialog",
    "append-to-body": "",
    "close-on-click-modal": false,
    "onUpdate:modelValue": onVisibilityChange
  }, {
    footer: _withCtx(() => [
      (
          step.value === 3 &&
          job.value &&
          (job.value.state === 'RUNNING' || job.value.state === 'PENDING')
        )
        ? (_openBlock(), _createBlock(_unref(ElButton), {
            key: 0,
            id: "datastore-import-cancel-job",
            size: "small",
            type: "danger",
            plain: "",
            onClick: onCancelJob
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("DataStore.CancelJob")), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (step.value > 0 && step.value < 3)
        ? (_openBlock(), _createBlock(_unref(ElButton), {
            key: 1,
            id: "datastore-import-back",
            size: "small",
            class: "btn-oneTeg3",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (step.value--))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("Common.Back")), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_unref(ElButton), {
        id: "datastore-import-close",
        size: "small",
        class: "btn-oneTeg3",
        onClick: close
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(step.value === 3 ? _unref(t)("DataStore.CloseJobContinues") : _unref(t)("Common.Cancel")), 1)
        ]),
        _: 1
      }),
      (step.value === 0)
        ? (_openBlock(), _createBlock(_unref(ElButton), {
            key: 2,
            id: "datastore-import-next-0",
            size: "small",
            class: "btn-oneTeg2",
            disabled: !preview.value,
            onClick: toMapping
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("DataStore.NextMapColumns")), 1)
            ]),
            _: 1
          }, 8, ["disabled"]))
        : (step.value === 1)
          ? (_openBlock(), _createBlock(_unref(ElButton), {
              key: 3,
              id: "datastore-import-next-1",
              size: "small",
              class: "btn-oneTeg2",
              onClick: toDryRun
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("DataStore.NextDryRun")), 1)
              ]),
              _: 1
            }))
          : (step.value === 2)
            ? (_openBlock(), _createBlock(_unref(ElButton), {
                key: 4,
                id: "datastore-import-start",
                size: "small",
                class: "btn-oneTeg2",
                onClick: startJob
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)("DataStore.StartImportJob")), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(ElSteps), {
        active: step.value,
        "align-center": "",
        class: "margin-bottom-10"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(ElStep), {
            title: _unref(t)('DataStore.StepUpload')
          }, null, 8, ["title"]),
          _createVNode(_unref(ElStep), {
            title: _unref(t)('DataStore.StepMapColumns')
          }, null, 8, ["title"]),
          _createVNode(_unref(ElStep), {
            title: _unref(t)('DataStore.StepDryRun')
          }, null, 8, ["title"]),
          _createVNode(_unref(ElStep), {
            title: _unref(t)('DataStore.StepImport')
          }, null, 8, ["title"])
        ]),
        _: 1
      }, 8, ["active"]),
      (step.value === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            (!preview.value)
              ? (_openBlock(), _createElementBlock("label", _hoisted_2, [
                  _createElementVNode("b", null, _toDisplayString(_unref(t)("DataStore.DropCsvHere")), 1)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("input", {
              id: "datastore-import-file",
              type: "file",
              accept: ".csv,text/csv",
              class: "datastore-file-input",
              onChange: onFilePicked
            }, null, 32),
            (preview.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("div", null, [
                    _createElementVNode("div", null, _toDisplayString(preview.value.fileName), 1),
                    _createElementVNode("div", _hoisted_4, _toDisplayString(formatBytes(preview.value.fileSize)) + " · " + _toDisplayString(_unref(t)("DataStore.EstimatedRows", {
                rows: formatCount(preview.value.estimatedRows),
              })), 1)
                  ]),
                  _createVNode(_unref(ElTag), {
                    size: "small",
                    type: "success"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)("DataStore.HeaderDetected")), 1)
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ]))
        : (step.value === 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(ElTable), {
                ref_key: "mapTable",
                ref: mapTable,
                class: "oneTeg-table oneTeg-table--sm",
                data: mapping.value,
                height: "40vh",
                "empty-text": _unref(t)('Common.NoData')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ElTableColumn), {
                    label: _unref(t)('DataStore.CsvColumn')
                  }, {
                    default: _withCtx((scope) => [
                      _createElementVNode("strong", null, _toDisplayString(scope.row.csvColumn), 1)
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_unref(ElTableColumn), {
                    label: _unref(t)('DataStore.Sample')
                  }, {
                    default: _withCtx((scope) => [
                      _createElementVNode("span", _hoisted_6, _toDisplayString(sampleFor(scope.$index)), 1)
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_unref(ElTableColumn), {
                    label: _unref(t)('DataStore.MapsToField'),
                    width: "200px"
                  }, {
                    default: _withCtx((scope) => [
                      _createVNode(_unref(ElSelect), {
                        id: 'datastore-map-target-' + scope.$index,
                        modelValue: scope.row.target,
                        "onUpdate:modelValue": ($event: any) => ((scope.row.target) = $event),
                        size: "mini",
                        class: "width-100"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.table.fields, (field) => {
                            return (_openBlock(), _createBlock(_unref(ElOption), {
                              key: field.id,
                              label: field.name,
                              value: field.id
                            }, null, 8, ["label", "value"]))
                          }), 128)),
                          _createVNode(_unref(ElOption), {
                            label: _unref(t)('DataStore.CreateNewField'),
                            value: _unref(MAP_CREATE_NEW)
                          }, null, 8, ["label", "value"]),
                          _createVNode(_unref(ElOption), {
                            label: _unref(t)('DataStore.SkipColumn'),
                            value: _unref(MAP_SKIP)
                          }, null, 8, ["label", "value"])
                        ]),
                        _: 2
                      }, 1032, ["id", "modelValue", "onUpdate:modelValue"])
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_unref(ElTableColumn), {
                    label: _unref(t)('DataStore.Type'),
                    width: "120px"
                  }, {
                    default: _withCtx((scope) => [
                      _createVNode(_unref(ElTag), { size: "small" }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(typeFor(scope.row)), 1)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 1
                  }, 8, ["label"])
                ]),
                _: 1
              }, 8, ["data", "empty-text"]),
              _createVNode(_unref(ElRadioGroup), {
                modelValue: importMode.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((importMode).value = $event)),
                class: "margin-top-10"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ElRadio), {
                    label: "append",
                    id: "datastore-import-append"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)("DataStore.AppendRows")), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(ElRadio), {
                    label: "upsert",
                    id: "datastore-import-upsert"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)("DataStore.UpsertByKey", { key: primaryFieldName.value })), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]))
          : (step.value === 2)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("DataStore.SampledRowsValidated")), 1),
                    _createElementVNode("b", null, _toDisplayString(formatCount(dryRun.value.sampled)), 1)
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("DataStore.ConvertibleRows")), 1),
                    _createElementVNode("b", _hoisted_11, _toDisplayString(formatCount(dryRun.value.ok)), 1)
                  ]),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("DataStore.FailingRows")), 1),
                    _createElementVNode("b", _hoisted_13, _toDisplayString(formatCount(dryRun.value.errors.length)), 1)
                  ])
                ]),
                (dryRun.value.errors.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _createElementVNode("ul", _hoisted_15, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dryRun.value.errors.slice(0, 30), (err, i) => {
                          return (_openBlock(), _createElementBlock("li", { key: i }, _toDisplayString(err), 1))
                        }), 128))
                      ]),
                      _createElementVNode("div", _hoisted_16, _toDisplayString(_unref(t)("DataStore.ErrorReportCapped")), 1)
                    ]))
                  : _createCommentVNode("", true),
                _createVNode(_unref(ElRadioGroup), {
                  modelValue: errorMode.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((errorMode).value = $event)),
                  class: "margin-top-10"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(ElRadio), {
                      label: "partial",
                      id: "datastore-import-partial"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("DataStore.PartialSave")), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(ElRadio), {
                      label: "strict",
                      id: "datastore-import-strict"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("DataStore.AllOrNothing")), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]))
            : (step.value === 3 && job.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                  _createElementVNode("div", _hoisted_18, [
                    _createElementVNode("div", null, [
                      _createElementVNode("div", null, _toDisplayString(_unref(t)("DataStore.ImportJob", { id: job.value.id })), 1)
                    ]),
                    _createVNode(_unref(ElTag), {
                      size: "small",
                      type: 
            job.value.state === 'FINISHED'
              ? 'success'
              : job.value.state === 'CANCELLED' || job.value.state === 'FAILED'
              ? 'danger'
              : 'warning'
          
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(job.value.state), 1)
                      ]),
                      _: 1
                    }, 8, ["type"])
                  ]),
                  _createVNode(_unref(ElProgress), {
                    percentage: job.value.progressPercentage,
                    "stroke-width": 10,
                    class: "margin-top-10"
                  }, null, 8, ["percentage"]),
                  _createElementVNode("div", _hoisted_19, [
                    _createElementVNode("span", null, _toDisplayString(formatCount(job.value.processedRows)) + " / " + _toDisplayString(formatCount(job.value.totalRows)) + " " + _toDisplayString(_unref(t)("DataStore.Rows").toLowerCase()), 1)
                  ]),
                  (job.value.report.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                        _createElementVNode("ul", _hoisted_21, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(job.value.report, (line, i) => {
                            return (_openBlock(), _createElementBlock("li", { key: i }, _toDisplayString(line), 1))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["model-value", "title"]))
}
}

})